import type { AppRoute } from 'src/routes/types';

import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuthContext } from 'src/legacy/auth/useAuthContext';
import IdleConfirmDialog from 'src/legacy/components/idleConfirmDialog/IdleConfirmDialog';
import { useSettingsContext } from 'src/legacy/components/settings';
import useResponsive from 'src/legacy/hooks/useResponsive';
import { getNativeLanguages, getStates } from 'src/legacy/redux/slices/common';
import { getPlacementList } from 'src/legacy/redux/slices/placement';
import { useDispatch, useSelector } from 'src/legacy/redux/store';
import { PATH_AUTH } from 'src/legacy/routes/paths';
import { useActiveCompany } from 'src/organization/hooks/useActiveCompany';

import Header from './header';
import Main from './Main';
import NavHorizontal from './nav/NavHorizontal';
import NavMini from './nav/NavMini';
import NavVertical from './nav/NavVertical';

//
type DashboardLayoutProps = {
  privateAppRoutes: AppRoute[];
};

const DashboardLayout = ({ privateAppRoutes }: DashboardLayoutProps) => {
  const dispatch = useDispatch();
  const { setVisible, themeLayout } = useSettingsContext();
  const { logout } = useAuthContext();
  const navigate = useNavigate();

  const { languages, states } = useSelector((state) => state.common);
  const { activeCompany } = useActiveCompany();
  const { placements } = useSelector((state) => state.placement);

  const isDesktop = useResponsive('up', 'lg');

  const [open, setOpen] = useState(false);

  const isNavHorizontal = themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const [isTimeout, setIsTimeout] = useState(false);

  const onIdle = () => {
    message('LOG_OUT', true);
  };

  const onPrompt = () => {
    setIsTimeout(true);
  };

  const onMessage = (message: string) => {
    if (message === 'LOG_OUT') {
      logout();
      navigate(PATH_AUTH.login, { replace: true });
    } else {
      setIsTimeout(false);
      reset();
    }
  };

  const { getRemainingTime, message, reset } = useIdleTimer({
    crossTab: true,
    leaderElection: true,
    name: 'idle-timer',
    onIdle,
    onMessage,
    onPrompt,
    promptBeforeIdle: 30_000,
    stopOnIdle: true,
    syncTimers: 200,
    timeout: 870_000,
  });

  useEffect(() => {
    setVisible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeCompany && !placements) {
      dispatch(getPlacementList({ company: activeCompany.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany, placements]);

  useEffect(() => {
    if (languages.length < 1) {
      dispatch(getNativeLanguages());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages]);

  useEffect(() => {
    if (states.length < 1) {
      dispatch(getStates());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const renderNavVertical = (
    <NavVertical
      onCloseNav={handleClose}
      openNav={open}
      privateAppRoutes={privateAppRoutes}
    />
  );

  if (isNavHorizontal) {
    return (
      <>
        <Header onOpenNav={handleOpen} privateAppRoutes={privateAppRoutes} />

        {isDesktop ? (
          <NavHorizontal privateAppRoutes={privateAppRoutes} />
        ) : (
          renderNavVertical
        )}
        {isTimeout && (
          <IdleConfirmDialog
            getRemainingTime={getRemainingTime}
            message={message}
            open={isTimeout}
          />
        )}
        <Main>
          <Outlet />
        </Main>
      </>
    );
  }

  if (isNavMini) {
    return (
      <>
        <Header onOpenNav={handleOpen} privateAppRoutes={privateAppRoutes} />

        <Box
          sx={{
            display: { lg: 'flex' },
            minHeight: { lg: 1 },
          }}
        >
          {isDesktop ? (
            <NavMini privateAppRoutes={privateAppRoutes} />
          ) : (
            renderNavVertical
          )}
          {isTimeout && (
            <IdleConfirmDialog
              getRemainingTime={getRemainingTime}
              message={message}
              open={isTimeout}
            />
          )}
          <Main>
            <Outlet />
          </Main>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header onOpenNav={handleOpen} privateAppRoutes={privateAppRoutes} />

      <Box
        sx={{
          display: { lg: 'flex' },
          minHeight: { lg: 1 },
        }}
      >
        {renderNavVertical}
        {isTimeout && (
          <IdleConfirmDialog
            getRemainingTime={getRemainingTime}
            message={message}
            open={isTimeout}
          />
        )}
        <Main>
          <Outlet />
        </Main>
      </Box>
    </>
  );
};

export default DashboardLayout;
